
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ChevronDown, MoreHorizontal, Plus, Pencil, Trash2, UserPlus, ChevronRight, UserMinus } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useExercisePlans } from "@/api/exercise-plans"
import { AddExercisePlanDialog } from "./add-exercise-plan-dialog"
import { EditExercisePlanDialog } from "./edit-exercise-plan-dialog"
import { DeleteExercisePlanDialog } from "./delete-exercise-plan-dialog"
import { AssignPlanModal } from "@/components/shared/assign-plan-modal"
import { useToast } from "@/hooks/use-toast"
import { DataTablePagination } from "@/components/ui/data-table-pagination"
import { TableEmptyRow, TableLoadingRows } from "@/components/shared/table-states"

// Define a type for the user
interface User {
  _id: string
  name: string
  surname: string
  email: string
  userType: string
  status: string
  gym: string[]
  dietPlans?: string[]
  exercisePlans?: string[]
}

export function ExercisePlanTable() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { data: exercisePlans, isLoading } = useExercisePlans()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [selectedPlanName, setSelectedPlanName] = useState<string>("")
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  // Toggle expanded row
  const toggleRow = (planId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }))
  }

  // Action handlers
  const openEditDialog = (plan: any) => {
    console.log("Opening edit dialog for plan:", plan)
    // Use _id instead of id
    setSelectedPlanId(plan._id)
    setSelectedPlanName(plan.name)
    setTimeout(() => setEditDialogOpen(true), 0)
  }

  const openDeleteDialog = (plan: any) => {
    console.log("Opening delete dialog for plan:", plan)
    // Use _id instead of id
    setSelectedPlanId(plan._id)
    setSelectedPlanName(plan.name)
    setTimeout(() => setDeleteDialogOpen(true), 0)
  }

  const openAssignDialog = (plan: any) => {
    console.log("Opening assign dialog for plan:", plan)
    // Use _id instead of id
    setSelectedPlanId(plan._id)
    setSelectedPlanName(plan.name)
    setTimeout(() => setAssignDialogOpen(true), 0)
  }

  const handleRemoveUserFromPlan = (planId: string, userId: string) => {
    // This will be implemented when you provide the endpoint
    console.log(`Remove user ${userId} from exercise plan ${planId}`)
    toast({
      title: "Not implemented yet",
      description: "This feature will be implemented when the endpoint is provided.",
    })
  }

  const columns: ColumnDef<any>[] = [
    {
      id: "expander",
      header: () => null,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => toggleRow(row.original._id)}
          aria-label={expandedRows[row.original._id] ? "Collapse row" : "Expand row"}
        >
          {expandedRows[row.original._id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      ),
    },
    {
      accessorKey: "name",
      header: t("exercisePlans.name"),
      cell: ({ row }) => <div className="font-medium">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "type",
      header: t("exercisePlans.type"),
      cell: ({ row }) => <div>{row.getValue("type")}</div>,
    },
    {
      accessorKey: "difficulty",
      header: t("exercisePlans.difficulty"),
      cell: ({ row }) => {
        const difficulty = row.getValue("difficulty") as string
        return (
          <Badge
            variant={difficulty === "beginner" ? "outline" : difficulty === "intermediate" ? "secondary" : "default"}
          >
            {t(`exercisePlans.${difficulty}`)}
          </Badge>
        )
      },
    },
    {
      accessorKey: "duration",
      header: t("exercisePlans.duration"),
      cell: ({ row }) => (
        <div>
          {row.getValue("duration")} {t("exercisePlans.minutes")}
        </div>
      ),
    },
    {
      accessorKey: "targetMuscles",
      header: t("exercisePlans.targetMuscles"),
      cell: ({ row }) => {
        const muscles = row.getValue("targetMuscles") as string[]
        return <div>{muscles.join(", ")}</div>
      },
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const plan = row.original

        return (
          <div className="relative">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">{t("common.openMenu")}</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => openAssignDialog(plan)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t("planAssignments.assignPlan")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => openEditDialog(plan)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openDeleteDialog(plan)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]

  const table = useReactTable({
    data: exercisePlans || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Input
            placeholder={t("common.search")}
            value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
            onChange={(event) => table.getColumn("name")?.setFilterValue(event.target.value)}
            className="w-full sm:max-w-sm"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> {t("exercisePlans.addPlan")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  {t("common.columns")} <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingRows colSpan={columns.length} />
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <>
                  <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                  {expandedRows[row.original._id] && (
                    <TableRow key={`${row.id}-expanded`} className="bg-muted/50">
                      <TableCell colSpan={columns.length} className="p-0">
                        <div className="p-4">
                          <h4 className="text-sm font-medium mb-2">{t("planAssignments.assignedUsers")}</h4>
                          {row.original.users && row.original.users.length > 0 ? (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>{t("users.name")}</TableHead>
                                  <TableHead>{t("users.email")}</TableHead>
                                  <TableHead>{t("users.status")}</TableHead>
                                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {row.original.users.map((user: User) => (
                                  <TableRow key={user._id}>
                                    <TableCell>{`${user.name} ${user.surname}`}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                      <Badge variant={user.status === "active" ? "default" : "secondary"}>
                                        {user.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleRemoveUserFromPlan(row.original._id, user._id)}
                                      >
                                        <UserMinus className="h-4 w-4" />
                                        <span className="sr-only">{t("planAssignments.removeUser")}</span>
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <p className="text-sm text-muted-foreground">{t("planAssignments.noAssignedUsers")}</p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))
            ) : (
              <TableEmptyRow colSpan={columns.length} message={t("exercisePlans.noPlans")} />
            )}
          </TableBody>
        </Table>
        <DataTablePagination
          page={table.getState().pagination.pageIndex + 1}
          pageCount={table.getPageCount()}
          total={table.getFilteredRowModel().rows.length}
          onPageChange={(p) => table.setPageIndex(p - 1)}
        />
      </CardContent>

      {/* Always render dialogs but control visibility with open prop */}
      <AddExercisePlanDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      {/* Only render dialogs when we have a valid planId */}
      {selectedPlanId && (
        <>
          <EditExercisePlanDialog planId={selectedPlanId} open={editDialogOpen} onOpenChange={setEditDialogOpen} />

          <DeleteExercisePlanDialog
            planId={selectedPlanId}
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          />

          <AssignPlanModal
            planType="exercise"
            open={assignDialogOpen}
            onOpenChange={setAssignDialogOpen}
          />
        </>
      )}
    </Card>
  )
}
